import { isApiLimitExceeded } from 'scenes/common/helpers/apiData';
import { YesNoAnswer } from 'scenes/common/types';
import {
  MotorFlowDefinition,
  MotorFlowType,
  MotorFormAnswer,
  MotorFunnelState,
  MotorStepType,
  VehicleSearchType,
} from 'scenes/motor/types';
import {
  getNumberOfAdditionalDrivers,
  getSelectedExtras,
  hasClaimsMade,
  isCoverUnder25Included,
  isUnder25CoverSelected,
  isVehicleUnderFinance,
  isVehicleUsedForBusiness,
} from 'scenes/motor/selectors';
import { defaultDriver } from 'scenes/motor/flows/addDriver';
import { OptionalExtra } from 'scenes/motor/flows/coverOptions/types';
import { skipPromoPage } from 'scenes/motor/helpers/campaign';

// When the regoSearch API limit is reached, we want to skip this step.
const toRegoSearchStepOr = (step: MotorStepType) => (
  funnelState: MotorFunnelState
) =>
  !isApiLimitExceeded(funnelState.apiResponses.foundVehicle)
    ? { step: MotorStepType.RegoSearch }
    : { step };

const manualVehicleEntryRequested = (funnelState: MotorFunnelState) =>
  funnelState.quoteDetails.vehicleSearchType === VehicleSearchType.ManualEntry;

const optionalExtras = (state: MotorFunnelState): string[] => {
  const extras = [...getSelectedExtras(state)];

  const under25Index = extras.indexOf(OptionalExtra.Under25);

  if (under25Index > -1 && !isUnder25CoverSelected(state)) {
    extras.splice(under25Index, 1);
  } else if (under25Index < 0 && isUnder25CoverSelected(state)) {
    extras.push(OptionalExtra.Under25);
  }

  return extras;
};

const stepToQuotePrice = (state: MotorFunnelState) => ({
  step: MotorStepType.QuotePrice,
  flow: MotorFlowType.CoverOptions,
  coverOptions: {
    ...state.coverOptions,
    selectedExtras: optionalExtras(state),
  },
});

export const forwardLinks: MotorFlowDefinition = {
  [MotorStepType.PolicyStartDate]: { step: MotorStepType.OvernightParking },
  [MotorStepType.OvernightParking]: toRegoSearchStepOr(
    MotorStepType.VehicleManualSelect
  ),
  [MotorStepType.RegoSearch]: (funnelState: MotorFunnelState) =>
    manualVehicleEntryRequested(funnelState)
      ? { step: MotorStepType.VehicleManualSelect }
      : { step: MotorStepType.VehicleDetails },
  [MotorStepType.VehicleDetails]: (funnelState: MotorFunnelState) =>
    manualVehicleEntryRequested(funnelState)
      ? { step: MotorStepType.VehicleManualSelect }
      : { step: MotorStepType.GenderAndDateOfBirth },
  [MotorStepType.VehicleManualSelect]: (funnelState: MotorFunnelState) =>
    manualVehicleEntryRequested(funnelState)
      ? { step: MotorStepType.GenderAndDateOfBirth }
      : { step: MotorStepType.RegoSearch },
  [MotorStepType.GenderAndDateOfBirth]: {
    step: MotorStepType.YearsDriving,
  },
  [MotorStepType.YearsDriving]: { step: MotorStepType.ClaimsMade },

  [MotorStepType.ClaimsMade]: (funnelState: MotorFunnelState) => ({
    step: hasClaimsMade(funnelState)
      ? MotorStepType.PayableClaims
      : MotorStepType.RecentInsurer,
  }),

  [MotorStepType.PayableClaims]: { step: MotorStepType.RecentInsurer },
  [MotorStepType.RecentInsurer]: { step: MotorStepType.UsedForBusiness },

  [MotorStepType.UsedForBusiness]: (funnelState: MotorFunnelState) => ({
    step: isVehicleUsedForBusiness(funnelState)
      ? MotorStepType.BusinessType
      : MotorStepType.Finance,
  }),
  [MotorStepType.BusinessType]: { step: MotorStepType.Finance },
  [MotorStepType.Finance]: (state: MotorFunnelState) => ({
    step: isVehicleUnderFinance(state)
      ? MotorStepType.VehicleFinancier
      : MotorStepType.AddDriver,
  }),

  [MotorStepType.VehicleFinancier]: { step: MotorStepType.AddDriver },
  [MotorStepType.AddDriver]: (
    state: MotorFunnelState,
    answer: MotorFormAnswer
  ) => {
    if (answer.formValues.addDriver === YesNoAnswer.Yes) {
      return {
        // If we're adding a driver, zero out that state and move
        // to the AddDriver flow.
        step: MotorStepType.GenderAndDateOfBirth,
        flow: MotorFlowType.AddDriver,
        [MotorFlowType.AddDriver]: {
          ...defaultDriver,
          index: getNumberOfAdditionalDrivers(state),
        },
      };
    }
    if (isCoverUnder25Included(state)) {
      return {
        [MotorFlowType.QuoteDetails]: {
          ...state[MotorFlowType.QuoteDetails],
          coverForDriversUnder25: YesNoAnswer.Yes,
        },
        step: MotorStepType.FlybuysNumber,
      };
    }
    return { step: MotorStepType.CoverForDriversUnder25 };
  },
  [MotorStepType.CoverForDriversUnder25]: {
    step: MotorStepType.FlybuysNumber,
  },
  [MotorStepType.FlybuysNumber]: (state: MotorFunnelState) =>
    skipPromoPage ? stepToQuotePrice(state) : { step: MotorStepType.PromoCode },
  [MotorStepType.PromoCode]: (state: MotorFunnelState) =>
    stepToQuotePrice(state),
};

export const backwardLinks: MotorFlowDefinition = {
  [MotorStepType.PolicyStartDate]: {},
  [MotorStepType.OvernightParking]: { step: MotorStepType.PolicyStartDate },
  [MotorStepType.RegoSearch]: { step: MotorStepType.OvernightParking },
  [MotorStepType.VehicleManualSelect]: toRegoSearchStepOr(
    MotorStepType.OvernightParking
  ),
  [MotorStepType.VehicleDetails]: toRegoSearchStepOr(
    MotorStepType.OvernightParking
  ),
  [MotorStepType.GenderAndDateOfBirth]: (funnelState: MotorFunnelState) =>
    manualVehicleEntryRequested(funnelState)
      ? { step: MotorStepType.VehicleManualSelect }
      : { step: MotorStepType.VehicleDetails },
  [MotorStepType.YearsDriving]: {
    step: MotorStepType.GenderAndDateOfBirth,
  },
  [MotorStepType.ClaimsMade]: { step: MotorStepType.YearsDriving },
  [MotorStepType.PayableClaims]: { step: MotorStepType.ClaimsMade },

  [MotorStepType.RecentInsurer]: (funnelState: MotorFunnelState) =>
    hasClaimsMade(funnelState)
      ? { step: MotorStepType.PayableClaims }
      : { step: MotorStepType.ClaimsMade },

  [MotorStepType.UsedForBusiness]: { step: MotorStepType.RecentInsurer },
  [MotorStepType.BusinessType]: { step: MotorStepType.UsedForBusiness },
  [MotorStepType.Finance]: (funnelState: MotorFunnelState) => ({
    step: isVehicleUsedForBusiness(funnelState)
      ? MotorStepType.BusinessType
      : MotorStepType.UsedForBusiness,
  }),
  [MotorStepType.VehicleFinancier]: { step: MotorStepType.Finance },
  [MotorStepType.AddDriver]: (funnelState: MotorFunnelState) => ({
    step: isVehicleUnderFinance(funnelState)
      ? MotorStepType.VehicleFinancier
      : MotorStepType.Finance,
  }),

  [MotorStepType.CoverForDriversUnder25]: {
    step: MotorStepType.AddDriver,
  },
  [MotorStepType.FlybuysNumber]: (funnelState: MotorFunnelState) => ({
    step: isCoverUnder25Included(funnelState)
      ? MotorStepType.AddDriver
      : MotorStepType.CoverForDriversUnder25,
  }),
  [MotorStepType.PromoCode]: { step: MotorStepType.FlybuysNumber },
};

export const editQuoteDetails = (
  funnelState: MotorFunnelState,
  action: MotorFormAnswer
) => ({
  step: action.targetStep,
  flow: MotorFlowType.QuoteDetails,
});
